import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/store';
import { redeemClientInvite } from '../lib/invite';
import type { PublicClientInvite } from '../lib/types';
import { Card, Notice, Spinner } from '../components/Shell';

/** What the pack actually asks for, said plainly before anyone commits to it. */
const STEPS = [
  {
    title: 'Your details',
    body: 'How to reach you, who to call if we cannot, and your vet.',
  },
  {
    title: 'One page per pet',
    body: 'Feeding, medication, habits and the things only you would know.',
  },
  {
    title: 'The services agreement',
    body: 'Read it and sign it from your phone. No printer, no scanner.',
  },
];

/**
 * The other end of a client invitation email.
 *
 * Unlike the staff invite, which redeems on arrival, this one leads with a
 * page: it goes to someone who has not agreed to anything yet, and a stranger
 * asked for a password before being told who is asking will simply close the
 * tab. So the business, the sitter's own message and what the pack involves
 * come first, and the account comes after.
 *
 * The token is not on its own a credential. The API matches it against the
 * signed-in caller's email, so a forwarded link is useless to whoever receives
 * it. That is why the address is fixed here rather than being a field.
 */
export function JoinInvite() {
  const { token } = useParams<{ token: string }>();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState<PublicClientInvite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  // Strict mode mounts effects twice in development, and redeeming writes.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token) return;
    void api<PublicClientInvite>(`/public/client-invites/${token}`, { anonymous: true })
      .then(setData)
      .catch((err) =>
        setLoadError(
          err instanceof ApiError
            ? err.message
            : 'That invitation could not be opened. Ask your sitter to send a new one.',
        ),
      );
  }, [token]);

  // Signed in already: there is nothing to ask for, so redeem and go.
  useEffect(() => {
    if (!token || !data || !session.user || attempted.current) return;
    attempted.current = true;
    setRedeeming(true);

    void (async () => {
      try {
        const { path, url } = await redeemClientInvite(token);
        if (url) window.location.assign(url);
        else navigate(path, { replace: true });
      } catch (caught) {
        setRedeemError(
          caught instanceof ApiError
            ? caught.message
            : 'That invitation could not be accepted. Ask your sitter to send a new one.',
        );
        setRedeeming(false);
      }
    })();
  }, [token, data, session.user, navigate]);

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <Notice tone="error" title="No invitation here">
          The link is missing its code. Use the button in the email instead.
        </Notice>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <h1 className="mb-3 text-3xl">This invitation is not usable</h1>
        <Notice tone="error">{loadError}</Notice>
        <p className="mt-6 text-[color:var(--color-ink-soft)]">
          If you already have an account,{' '}
          <Link to="/sign-in" className="underline">
            sign in
          </Link>{' '}
          and everything will be where you left it.
        </p>
      </div>
    );
  }

  if (!data || session.status === 'loading') {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <Spinner label="Opening your invitation" />
      </div>
    );
  }

  const { invite, tenant } = data;
  const greetingName = invite.invitedName?.split(' ')[0];

  return (
    <div className="mx-auto max-w-xl px-5 pb-24 pt-16">
      <p className="eyebrow mb-2">{tenant.businessName}</p>
      <h1 className="text-3xl sm:text-4xl">
        {greetingName ? `${greetingName}, let’s set up your pet’s file` : 'Let’s set up your pet’s file'}
      </h1>
      <p className="mt-3 text-[color:var(--color-ink-soft)]">
        {tenant.businessName} invited you to fill in their new client pack. It takes about ten minutes and
        you can do all of it from your phone.
      </p>

      {invite.message && (
        <blockquote className="mt-6 border-l-[3px] border-[color:var(--color-stamp)] pl-4 text-[color:var(--color-ink-soft)] italic">
          {invite.message}
        </blockquote>
      )}

      <Card className="mt-8 p-6">
        <ol className="space-y-5">
          {STEPS.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span
                aria-hidden
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-sheet)] text-sm font-bold"
              >
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="font-display font-bold">{step.title}</p>
                <p className="text-sm text-[color:var(--color-ink-soft)]">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      {redeemError && (
        <div className="mt-8">
          <Notice tone="error" title="That did not work">
            {redeemError}
          </Notice>
        </div>
      )}

      {redeeming ? (
        <div className="mt-8">
          <Spinner label="Opening your file" />
        </div>
      ) : (
        !session.user && (
          <div className="mt-8">
            <p className="mb-4 text-sm text-[color:var(--color-ink-soft)]">
              You will need an account so your records stay yours. It goes with{' '}
              <span className="typed">{invite.email}</span>, the address this invitation was sent to.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                className="btn btn-primary"
                to="/sign-up"
                state={{ from: location.pathname, inviteToken: token, email: invite.email, name: invite.invitedName }}
              >
                Create my account
              </Link>
              <Link
                className="btn btn-secondary"
                to="/sign-in"
                state={{ from: location.pathname, email: invite.email }}
              >
                I already have one
              </Link>
            </div>
          </div>
        )
      )}
    </div>
  );
}
